import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GitMerge, RefreshCw, ShieldCheck, Sparkles } from "lucide-react";
import {
  designateCanonical,
  discoverStub,
  listConflicts,
  listRecommendations,
  type CanonicalRecommendation,
  type ConflictReport,
} from "@/lib/api";

/**
 * Conflicts page — the spec's flagship view per User Story 1
 * (§4): "show me every definition of MRR across all our BI
 * platforms and which ones disagree."
 *
 * Three sources of truth wired:
 * - GET /conflicts — clusters with > 1 variant + narrative
 * - GET /recommendations — canonical scorer's per-cluster pick
 * - POST /trusted-definitions — designate the recommendation
 */
export function ConflictsPage() {
  const tenantId = "_default";
  const qc = useQueryClient();

  const conflicts = useQuery({
    queryKey: ["semantic-conflicts", tenantId],
    queryFn: () => listConflicts(tenantId),
  });

  const recommendations = useQuery({
    queryKey: ["semantic-recommendations", tenantId],
    queryFn: () => listRecommendations(tenantId),
    // Recommendations come from the same data; only fetch when conflicts
    // loaded successfully.
    enabled: conflicts.isSuccess,
  });

  const discover = useMutation({
    mutationFn: () => discoverStub(tenantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["semantic-conflicts", tenantId] });
      qc.invalidateQueries({ queryKey: ["semantic-recommendations", tenantId] });
    },
  });

  // Index recommendations by clusterId so the conflict cards can
  // render them inline.
  const recsByCluster = useMemo(() => {
    const m = new Map<string, CanonicalRecommendation>();
    if (recommendations.data) {
      for (const r of recommendations.data.recommendations) {
        m.set(r.clusterId, r);
      }
    }
    return m;
  }, [recommendations.data]);

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-semantic-ink">
            Metric conflicts
          </h1>
          <p className="mt-0.5 text-sm text-semantic-slate">
            Definitions that disagree across the connected BI platforms.
            Pick a canonical and write it through the trusted catalog.
          </p>
        </div>
        <button
          type="button"
          onClick={() => discover.mutate()}
          disabled={discover.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-semantic-primary px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-semantic-primaryDark disabled:opacity-60"
        >
          <RefreshCw size={12} className={discover.isPending ? "animate-spin" : undefined} />
          Run discovery
        </button>
      </header>

      {(conflicts.isLoading || recommendations.isLoading) && <Skeleton />}
      {conflicts.error && (
        <ErrorBanner message={(conflicts.error as Error).message} />
      )}
      {conflicts.data && conflicts.data.conflicts.length === 0 && (
        <EmptyState
          definitionCount={conflicts.data.definitionCount}
          clusterCount={conflicts.data.clusterCount}
        />
      )}
      {conflicts.data && conflicts.data.conflicts.length > 0 && (
        <ul data-tour-id="conflicts-list" className="space-y-4">
          {conflicts.data.conflicts.map((c) => (
            <li key={c.clusterId}>
              <ConflictCard
                conflict={c}
                recommendation={recsByCluster.get(c.clusterId)}
                onDesignate={async (definitionId, reason) => {
                  await designateCanonical(tenantId, {
                    definitionId,
                    approver: "ui-user",
                    reason,
                  });
                  await qc.invalidateQueries({
                    queryKey: ["semantic-trusted", tenantId],
                  });
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ConflictCard({
  conflict,
  recommendation,
  onDesignate,
}: {
  conflict: ConflictReport;
  recommendation: CanonicalRecommendation | undefined;
  onDesignate: (definitionId: string, reason: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [pickedId, setPickedId] = useState<string | undefined>(
    recommendation?.recommendedDefinitionId,
  );
  const [reason, setReason] = useState(
    "Approving recommended canonical after review.",
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const recommendedId = recommendation?.recommendedDefinitionId;

  return (
    <div className="rounded-xl2 border border-semantic-slate/20 bg-white p-4 shadow-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm">
            <GitMerge size={14} className="text-semantic-slate" aria-hidden />
            <span className="font-medium text-semantic-ink">{conflict.displayName}</span>
            <span className="text-2xs text-semantic-slate">
              · {conflict.variantCount} variants
            </span>
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-semantic-ink">
            {conflict.narrative}
          </p>
        </div>
        {recommendation && (
          <div data-tour-id="conflicts-recommend" className="rounded-lg border border-semantic-primary/30 bg-semantic-primary/5 px-3 py-2 text-2xs">
            <div className="flex items-center gap-1 font-medium text-semantic-primary">
              <Sparkles size={11} /> Recommended
            </div>
            <div className="font-mono text-semantic-ink">
              {recommendation.recommendedDefinitionId}
            </div>
            <div className="text-semantic-slate">
              confidence {(recommendation.confidence * 100).toFixed(0)}%
            </div>
          </div>
        )}
      </div>

      <ul className="mt-3 grid gap-2 sm:grid-cols-2">
        {conflict.variants.map((v) => (
          <li key={v.definitionId}>
            <label
              className={`flex cursor-pointer items-start gap-2 rounded-lg border p-2.5 ${
                pickedId === v.definitionId
                  ? "border-semantic-primary bg-semantic-primary/5"
                  : "border-semantic-slate/20"
              }`}
            >
              <input
                type="radio"
                name={conflict.clusterId}
                value={v.definitionId}
                checked={pickedId === v.definitionId}
                onChange={() => setPickedId(v.definitionId)}
                className="mt-1 accent-semantic-primary"
              />
              <div className="flex-1">
                <div className="flex items-center gap-1.5 text-2xs">
                  <PlatformBadge platform={v.platform} />
                  <span className="text-semantic-slate">owner {v.owner}</span>
                  {v.definitionId === recommendedId && (
                    <span className="rounded-full bg-semantic-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-semantic-primary">
                      scorer's pick
                    </span>
                  )}
                </div>
                <pre className="mt-1 overflow-x-auto rounded bg-semantic-ink/5 px-2 py-1 font-mono text-2xs text-semantic-ink">
                  {v.formula}
                </pre>
              </div>
            </label>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="flex-1 text-2xs text-semantic-slate">
          <span>Reason (audit-logged)</span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full rounded-md border border-semantic-slate/30 bg-white px-2 py-1 text-sm text-semantic-ink focus:border-semantic-primary focus:outline-none focus:ring-2 focus:ring-semantic-primary/20"
          />
        </label>
        <button
          type="button"
          data-tour-id="conflicts-designate"
          disabled={!pickedId || busy || reason.trim().length === 0}
          onClick={async () => {
            if (!pickedId) return;
            setBusy(true);
            setError(null);
            setSuccess(null);
            try {
              await onDesignate(pickedId, reason.trim());
              setSuccess("Designated — see Trusted catalog.");
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(false);
            }
          }}
          className="inline-flex items-center gap-1.5 rounded-md bg-semantic-primary px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-semantic-primaryDark disabled:opacity-60"
        >
          <ShieldCheck size={12} /> Designate canonical
        </button>
      </div>
      {error && (
        <p className="mt-2 text-2xs text-semantic-danger">Couldn't designate — {error}</p>
      )}
      {success && (
        <p className="mt-2 text-2xs text-semantic-success">{success}</p>
      )}
    </div>
  );
}

function PlatformBadge({ platform }: { platform: string }) {
  return (
    <span className="rounded-full bg-semantic-mist px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-semantic-ink">
      {platform}
    </span>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3">
      {[0, 1].map((i) => (
        <div key={i} className="h-32 animate-pulse rounded-xl2 bg-semantic-mist/60" />
      ))}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-xl2 border border-semantic-danger/30 bg-semantic-danger/5 p-4 text-sm text-semantic-danger">
      Couldn't load conflicts — {message}
    </div>
  );
}

function EmptyState({
  definitionCount,
  clusterCount,
}: {
  definitionCount: number;
  clusterCount: number;
}) {
  return (
    <div className="rounded-xl2 border border-dashed border-semantic-slate/30 bg-white p-10 text-center">
      <Sparkles size={28} aria-hidden className="mx-auto text-semantic-primary/60" />
      <p className="mt-2 text-sm font-medium text-semantic-ink">
        No conflicts in scope.
      </p>
      <p className="mt-1 text-2xs text-semantic-slate">
        {definitionCount === 0
          ? "Run discovery to populate the metadata graph from connected platforms."
          : `${definitionCount} definitions across ${clusterCount} clusters; every cluster has only one variant.`}
      </p>
    </div>
  );
}
